import Hls, { FragChangedData } from 'hls.js';
import { useCallback } from 'react';
import Player from 'video.js/dist/types/player';

/**
 * Sets up HLS player to sync wall-clock time from stream PDT (Program Date Time) and
 * call onClockUpdate on each timeupdate. Uses FRAG_CHANGED to get segment start time
 * and interpolates current time from playhead position.
 */
export function useHlsClockSync(onClockUpdate?: (time: Date) => void) {
  const onReady = useCallback(
    (player: Player) => {
      player.el().classList.add('vjs-no-pause');

      const hls = (player as { hls?: Hls }).hls;

      hls?.on(Hls.Events.FRAG_CHANGED, (_event: unknown, data: FragChangedData) => {
        const media = hls?.media;

        if (!data?.frag?.programDateTime || !media) {
          return;
        }

        const updateClock = () => {
          const frag = data.frag;

          if (!frag) return;

          // (Segment Start Time) + (Current Playhead - Segment Start Pos), in ms
          const driftOffset = (media.currentTime - frag.start) * 1000;
          const actualTime = new Date(frag.programDateTime! + driftOffset);

          onClockUpdate?.(actualTime);
        };

        media.addEventListener('timeupdate', updateClock);

        player.on('dispose', () => {
          media.removeEventListener('timeupdate', updateClock);
        });
      });
    },
    [onClockUpdate],
  );

  return onReady;
}
